/**
 * A conta na corretora (spec 043, secao 4): a situacao da conta (ativa, bloqueada, bloqueada por risco), o saldo com a
 * idade, o saldo inicial e o resultado. So desenha: estado, saldo e resultado vem prontos do backend, e quem escolhe a
 * frase de cada situacao e o app (o cliente e o administrador leem frases diferentes). Nao sabe marca.
 */
import type { ReactNode } from "react";
import { formatarMoeda, formatarResultado, idadeDaLeitura } from "../formatos";
import { IconeAlerta, IconeCadeado, IconeCheck, IconeInfo, IconeRelogio } from "../icones";
import type { Tom } from "./Basicos";

export type EstadoNaCorretora = "ATIVA" | "BLOQUEADA" | "DESATIVADA" | "NAO_ENCONTRADA" | "SEM_LEITURA" | "LEITURA_DESLIGADA";

/** Rotulo e tom de cada estado da conta, como o backend manda. A area do cliente so mostra os tres primeiros. */
export const ESTADO_NA_CORRETORA: Record<EstadoNaCorretora, { rotulo: string; tom: Tom }> = {
  ATIVA: { rotulo: "Ativa", tom: "sucesso" },
  BLOQUEADA: { rotulo: "Bloqueada na corretora", tom: "erro" },
  DESATIVADA: { rotulo: "Desativada na corretora", tom: "erro" },
  NAO_ENCONTRADA: { rotulo: "Não encontrada na corretora", tom: "alerta" },
  SEM_LEITURA: { rotulo: "Sem leitura", tom: "alerta" },
  LEITURA_DESLIGADA: { rotulo: "Leitura desligada", tom: "neutro" },
};

/** Por que nao ha saldo pra mostrar (spec 043, 3.5): cada causa tem a sua frase, escolhida pelo app. */
export type MotivoSemSaldo = "LEITURA_DESLIGADA" | "SEM_CONTA" | "AINDA_NAO_VISTA" | "PLANO_ENCERRADO";

/** O resumo do modulo `saldo` de uma matricula, como o backend manda (spec 043): centavos, moeda ISO e quando foi visto. */
export type ResumoDoSaldo = {
  conta: string | null;
  moeda: string | null;
  saldo_centavos: number | null;
  visto_em: string | null;
  motivo_sem_saldo: MotivoSemSaldo | null;
  saldo_inicial_centavos: number | null;
  saldo_inicial_moeda: string | null;
  /** Saldo menos o inicial, calculado no backend. Nulo: sem base pra calcular (nunca zero). */
  resultado_centavos: number | null;
  bloqueada_por_risco: boolean | null;
  motivo_do_risco: string | null;
  risco_visto_em: string | null;
};

/**
 * O resumo do saldo pronto pro `SaldoNaCorretora`: o saldo com a moeda e a idade, o inicial e o resultado com o tom do
 * sinal. Nao calcula nada: o resultado vem do backend. Sem saldo, o resultado nao aparece.
 */
export function numerosDoSaldo(s: ResumoDoSaldo, agora = new Date()) {
  const saldo = s.saldo_centavos != null && s.visto_em ? { valor: formatarMoeda(s.saldo_centavos, s.moeda), idade: idadeDaLeitura(s.visto_em, agora) } : null;
  const inicial = s.saldo_inicial_centavos != null ? formatarMoeda(s.saldo_inicial_centavos, s.saldo_inicial_moeda) : null;
  const r = s.resultado_centavos;
  const resultado = !saldo ? undefined : r == null ? null : { valor: formatarResultado(r, s.moeda), tom: r > 0 ? ("sucesso" as const) : r < 0 ? ("erro" as const) : ("neutro" as const) };
  return { saldo, inicial, resultado };
}

export type TomDaSituacao = Extract<Tom, "neutro" | "sucesso" | "alerta" | "erro" | "info">;
/** Uma linha de situacao da conta: "Ativa", "Bloqueada na corretora", "Bloqueada por risco: <frase da corretora>". */
export type SituacaoDaConta = { tom: TomDaSituacao; titulo: ReactNode; detalhe?: ReactNode };

const ICONE: Record<TomDaSituacao, typeof IconeInfo> = { sucesso: IconeCheck, erro: IconeCadeado, alerta: IconeAlerta, info: IconeInfo, neutro: IconeInfo };

/**
 * O saldo da conta na corretora, sempre com a idade ("visto há 5 min"): saldo velho nao se disfarca de atual. Sem
 * saldo, `semSaldo` diz por que. `inicial` e `resultado` ausentes nao aparecem; nulos dizem que falta a base
 * ("Sem base pra calcular", nunca zero).
 */
export function SaldoNaCorretora({ rotulo = "Saldo na corretora", situacoes = [], saldo, semSaldo, inicial, semInicial = "Não informado", resultado }: {
  rotulo?: string;
  situacoes?: SituacaoDaConta[];
  /** Ja formatado, com a moeda e a idade. Nulo: sem saldo (diga por que em `semSaldo`). Ausente: so a situacao. */
  saldo?: { valor: string; idade: string } | null;
  semSaldo?: ReactNode;
  inicial?: string | null;
  semInicial?: string;
  resultado?: { valor: string; tom: "sucesso" | "erro" | "neutro" } | null;
}) {
  return (
    <section className="mtc-corretora" aria-label={rotulo}>
      {situacoes.length > 0 && (
        <ul className="mtc-corretora__situacoes">
          {situacoes.map((s, i) => {
            const Icone = ICONE[s.tom];
            return (
              <li key={i} className={`mtc-corretora__situacao mtc-corretora__situacao--${s.tom}`}>
                <Icone tamanho={16} aria-hidden="true" />
                <span>
                  <strong>{s.titulo}</strong>
                  {s.detalhe && <small>{s.detalhe}</small>}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {saldo !== undefined && (
        <div className="mtc-corretora__saldo">
          <span className="mtc-corretora__rotulo">{rotulo}</span>
          {saldo ? (
            <>
              <strong className="mtc-corretora__valor">{saldo.valor}</strong>
              <span className="mtc-corretora__idade"><IconeRelogio tamanho={14} aria-hidden="true" />{saldo.idade}</span>
            </>
          ) : (
            <p className="mtc-corretora__sem">{semSaldo ?? "Sem saldo pra mostrar agora."}</p>
          )}
        </div>
      )}
      {(inicial !== undefined || resultado !== undefined) && (
        <dl className="mtc-corretora__numeros">
          {inicial !== undefined && (
            <div>
              <dt>Saldo inicial</dt>
              <dd>{inicial ? <span className="mtc-mono">{inicial}</span> : <span className="mtc-corretora__falta">{semInicial}</span>}</dd>
            </div>
          )}
          {resultado !== undefined && (
            <div>
              <dt>Resultado</dt>
              <dd>{resultado ? <span className={`mtc-mono mtc-corretora__resultado mtc-corretora__resultado--${resultado.tom}`}>{resultado.valor}</span> : <span className="mtc-corretora__falta">Sem base pra calcular</span>}</dd>
            </div>
          )}
        </dl>
      )}
    </section>
  );
}
